import { useState } from 'react'
import { InquiryType } from '../../data/contact'

export type ContactFields = {
  firstName: string
  lastName: string
  email: string
  organization: string
  inquiryType: InquiryType
  message: string
}

type FieldErrors = Partial<Record<keyof ContactFields, string>>
type Status = 'idle' | 'submitting' | 'success'

const emptyFields: ContactFields = {
  firstName: '',
  lastName: '',
  email: '',
  organization: '',
  inquiryType: '',
  message: '',
}

export function useContactForm() {
  const [fields, setFields] = useState<ContactFields>(emptyFields)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [status, setStatus] = useState<Status>('idle')

  const update = <K extends keyof ContactFields>(key: K, value: ContactFields[K]) => {
    setFields((current) => ({ ...current, [key]: value }))
    if (errors[key]) {
      setErrors((current) => ({ ...current, [key]: undefined }))
    }
  }

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextErrors = validate(fields)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      const firstInvalid = Object.keys(nextErrors)[0]
      document.getElementById(firstInvalid)?.focus()
      return
    }
    setStatus('submitting')
    window.setTimeout(() => setStatus('success'), 700)
  }

  const reset = () => {
    setFields(emptyFields)
    setErrors({})
    setStatus('idle')
  }

  return { fields, errors, status, update, submit, reset }
}

function validate(fields: ContactFields): FieldErrors {
  const errors: FieldErrors = {}
  if (!fields.firstName.trim()) errors.firstName = 'Enter your first name.'
  if (!fields.lastName.trim()) errors.lastName = 'Enter your last name.'
  if (!fields.email.trim()) {
    errors.email = 'Enter your email address.'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
    errors.email = 'Enter a valid email address.'
  }
  if (!fields.inquiryType) errors.inquiryType = 'Choose what your message is about.'
  if (!fields.message.trim()) errors.message = 'Tell us a little about your inquiry.'
  return errors
}
